
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { toast } from 'sonner';

export interface OrderItem {
  name: string;
  price: number;
  productId: string;
  quantity: number;
}

export interface Order {
  id: string;
  customerName: string;
  email: string;
  address: string;
  city: string;
  phone: string;
  paymentMethod: string;
  items: OrderItem[];
  total: number;
  status: 'pending' | 'processing' | 'completed' | 'cancelled';
  createdAt: string;
}

// Fetch all orders
export const useOrders = () => {
  return useQuery({
    queryKey: ['orders'],
    queryFn: async (): Promise<Order[]> => {
      try {
        const ordersRef = collection(db, 'orders');
        const querySnapshot = await getDocs(ordersRef);
        
        return querySnapshot.docs.map(doc => {
          const data = doc.data();
          
          // Calculate total from items if not present
          let total = 0;
          if (data.total && typeof data.total === 'number') {
            total = data.total;
          } else if (data.items && Array.isArray(data.items)) {
            total = data.items.reduce((sum, item) => {
              return sum + (Number(item.price) * Number(item.quantity));
            }, 0);
          }
          
          return {
            id: doc.id,
            customerName: data.customerName || '',
            email: data.email || '',
            address: data.address || '',
            city: data.city || '',
            phone: data.phone || '',
            paymentMethod: data.paymentMethod || '',
            items: Array.isArray(data.items) ? data.items : [],
            total,
            status: data.status || 'pending',
            createdAt: formatTimestamp(data.createdAt),
          };
        });
      } catch (error) {
        console.error('Error fetching orders:', error);
        throw error;
      }
    },
  });
};

// Get a single order by ID
export const useOrder = (orderId: string) => {
  return useQuery({
    queryKey: ['order', orderId],
    queryFn: async (): Promise<Order | null> => {
      try {
        if (!orderId) return null;
        
        const docRef = doc(db, 'orders', orderId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          
          // Calculate total from items if not present
          let total = 0;
          if (data.total && typeof data.total === 'number') {
            total = data.total;
          } else if (data.items && Array.isArray(data.items)) {
            total = data.items.reduce((sum, item) => {
              return sum + (Number(item.price) * Number(item.quantity));
            }, 0);
          }
          
          return {
            id: docSnap.id,
            customerName: data.customerName || '',
            email: data.email || '',
            address: data.address || '',
            city: data.city || '',
            phone: data.phone || '',
            paymentMethod: data.paymentMethod || '',
            items: Array.isArray(data.items) ? data.items : [],
            total,
            status: data.status || 'pending',
            createdAt: formatTimestamp(data.createdAt),
          };
        }
        return null;
      } catch (error) {
        console.error('Error fetching order:', error);
        throw error;
      }
    },
    enabled: !!orderId,
  });
};

// Update an order
export const useUpdateOrder = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ orderId, data }: { orderId: string; data: Partial<Order> }) => {
      try {
        const orderRef = doc(db, 'orders', orderId);
        await updateDoc(orderRef, {
          ...data,
          lastUpdated: new Date().toISOString()
        });
        return { id: orderId, ...data };
      } catch (error) {
        console.error('Error updating order:', error);
        throw error;
      }
    },
    onSuccess: () => {
      toast.success('Order updated successfully');
      // Invalidate and refetch the orders list
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      // Also invalidate the single order query
      queryClient.invalidateQueries({ queryKey: ['order'] });
    },
    onError: () => {
      toast.error('Failed to update order');
    }
  });
};

// Helper function to safely format timestamps
const formatTimestamp = (timestamp: any): string => {
  if (!timestamp) return new Date().toISOString();
  
  try {
    // Handle Firestore Timestamp objects
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toISOString();
    }
    
    // Handle timestamp objects with seconds and nanoseconds
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toISOString();
    }
    
    // Handle ISO string format
    if (typeof timestamp === 'string') {
      return new Date(timestamp).toISOString();
    }
    
    // Handle numeric timestamps (milliseconds since epoch)
    if (typeof timestamp === 'number') {
      return new Date(timestamp).toISOString();
    }
    
    // Default fallback
    return new Date().toISOString();
  } catch (error) {
    console.error('Error formatting timestamp:', error, timestamp);
    return new Date().toISOString();
  }
};
